// GLSL for the porch. One set of light uniforms is shared by every lit program (setLights in renderer.ts).

export const MAXP = 12;

export const COMMON = /* glsl */`
#define MAXP ${MAXP}
#define PI 3.14159265
#define SHEET_PHI 1.25
#define SHEET_THETA 1.0
uniform vec3 uEye;
uniform float uTime;
uniform vec3 uMoonDir, uMoon, uSkyAmb, uGroundAmb;
uniform vec3 uLampPos, uLamp;
uniform float uWall;
uniform mat4 uLampVP;
uniform int uNP;
uniform vec3 uPC[MAXP];       // pumpkin centre, world
uniform vec3 uPL[MAXP];       // its light now: colour times power
uniform mat3 uPRot[MAXP];     // world to pumpkin space
uniform vec3 uPSrc[MAXP];     // where the light is, pumpkin space
uniform vec4 uPGeo[MAXP];     // bulb radius, squash, softness (mip), glow
uniform sampler2DArray uMasks;
uniform int uLayer[MAXP];     // each pumpkin's layer in uMasks
uniform vec2 uPOpen[MAXP];     // how open each shell is, cut and scraped (carve.ts)
uniform sampler2D uDash;      // the dashed outline of the stencil being carved
uniform vec4 uMist;           // rgb, density
uniform float uExposure;
uniform float uUnder;         // 1 while drawing the stage floor's reflection: what is below the floor isn't reflected
uniform int uNS;              // spots: the stage's three, or the porch's one in the garden
uniform mat4 uSpotVP[3];      // each spot's view: its frustum is its cone, its quarter of the shadow map
uniform vec3 uSpotPos[3], uSpotCol[3];

/** HDR tiers hand the light on to post; the plain raster tier tone-maps here and draws straight to the screen */
vec4 finish(vec3 c) {
#ifdef LDR
  c *= uExposure;
  c = clamp((c * (2.51 * c + 0.03)) / (c * (2.43 * c + 0.59) + 0.14), 0.0, 1.0);
  return vec4(pow(c, vec3(1.0 / 2.2)), 1.0);
#else
  return vec4(c, 1.0);
#endif
}
#ifdef SHADOWS
uniform sampler2DShadow uShadow;
uniform float uShadowTexel;
#endif

uint pcg(inout uint s) { s = s * 747796405u + 2891336453u; uint w = ((s >> ((s >> 28u) + 4u)) ^ s) * 277803737u; return (w >> 22u) ^ w; }
float hash12(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
float vnoise(vec2 p) {
  vec2 i = floor(p), f = fract(p), u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash12(i), hash12(i + vec2(1, 0)), u.x), mix(hash12(i + vec2(0, 1)), hash12(i + vec2(1, 1)), u.x), u.y);
}
float fbm(vec2 p) { return vnoise(p) * 0.5 + vnoise(p * 2.03 + 7.1) * 0.3 + vnoise(p * 4.1 + 3.3) * 0.2; }
// how much of a pattern at this frequency survives at this pixel's footprint: 1 well under a cycle a pixel, 0 past
// half; a faded pattern goes to its mean instead of shimmering
float keep(vec2 freq, vec2 fw) { return clamp(1.5 - 2.5 * max(freq.x * fw.x, freq.y * fw.y), 0.0, 1.0); }

/** where on the stencil's sheet a point of pumpkin space falls, as it looks from the centre */
vec2 sheetOf(vec3 q, float squash) {
  return vec2(atan(q.x, q.z) / SHEET_PHI, atan(q.y / squash, length(q.xz)) / SHEET_THETA);
}
// the carving covers the whole shell, round and over the top: its texture runs once round the pumpkin, top row first
vec2 carveUV(vec2 sheet) { return vec2(sheet.x * SHEET_PHI / (2.0 * PI) + 0.5, 0.5 - sheet.y * SHEET_THETA / PI); }
/** r cut through, g carved depth (1 through), b the stencil's trace on the skin */
vec3 maskAt(int i, vec2 sheet, float lod) {
  vec3 m = textureLod(uMasks, vec3(carveUV(sheet), float(uLayer[i])), lod).rgb;
  // the canvas is uploaded raw: a full scrape counts as cut, and a cut clears the carve and the trace under it
  m.r = max(m.r, smoothstep(0.96, 0.99, m.g));
  return vec3(m.r, m.g * (1.0 - m.r), m.b * (1.0 - m.r));
}

/**
 * the light of pumpkin i reaching world point p: from its source, out through the shell where the stencil is cut (and a
 * little where it is scraped), found where the ray leaves the bulb. The shell is a sphere of the bulb's radius here,
 * squashed as the pumpkin is: close enough for light thrown on the boards. Returns the light and its direction.
 */
vec3 pumpkinLight(int i, vec3 p, out vec3 L) {
  vec3 q = uPRot[i] * (p - uPC[i]);
  vec4 g = uPGeo[i];
  // a shortcut: the light is thrown from high in the shell and a little back, not from the candle at the bottom, so the
  // face lands on the boards in front (upside down, as a lantern's does) instead of going out over everything
  vec3 src = mix(uPSrc[i], vec3(0.0, 0.55 * g.x * g.y, -0.3 * g.x), 0.85);
  vec3 qs = vec3(q.x, q.y / g.y, q.z), ss = vec3(src.x, src.y / g.y, src.z);
  vec3 d = qs - ss;
  float dist2 = dot(q - src, q - src);
  L = normalize(transpose(uPRot[i]) * (src - q));
#ifdef PLAIN_LIGHT
  return uPL[i] * 0.9 / (dist2 + 0.002);   // Low: a plain light, not thrown through the carving
#endif
  float R = g.x;
  if (dot(qs, qs) < R * R * 0.81) return uPL[i] / (dist2 + 0.002);   // inside: lit directly
  vec3 dn = normalize(d);
  float b = dot(ss, dn), c = dot(ss, ss) - R * R;
  float t = -b + sqrt(max(b * b - c, 0.0));
  vec3 x = ss + dn * t;
  // the face thrown on whatever is near: sharp close to the shell, its edges spreading with the distance out (the
  // flame is a few centimetres across, not a point)
  float out_ = length(qs - x);
  float lod = clamp(g.z + log2(1.0 + 3.0 * out_ / R), 0.0, 7.0);
  vec3 m = maskAt(i, sheetOf(vec3(x.x, x.y * g.y, x.z), g.y), lod);
  // and a little through the flesh everywhere, a warm wash round the foot
  float through = 3.0 * m.r + (0.6 * m.g * m.g + 0.18) * (1.0 - m.r);
  // one candle's worth: a shell cut or scraped away nearly all over would otherwise throw like a bare bulb and wash
  // the whole porch
  vec2 a = uPOpen[i];
  float open_ = 3.0 * a.x + (0.6 * a.y + 0.18) * (1.0 - a.x);
  return uPL[i] * through * 2.1 * clamp(0.36 / open_, 0.25, 1.0) / (dist2 + 0.002);
}

float lampShadow(vec3 p, vec3 n) {
#ifdef SHADOWS
  vec4 c = uLampVP * vec4(p + n * 0.02, 1.0);
  vec3 s = c.xyz / c.w * 0.5 + 0.5;
  if (s.x < 0.0 || s.y < 0.0 || s.x > 1.0 || s.y > 1.0 || c.w < 0.0) return 1.0;
  s.xy = clamp(s.xy, 0.004, 0.996) * 0.5 + 0.5;   // the lantern has the last quarter of the map
  float o = uShadowTexel, z = s.z - 0.0015;
  return 0.25 * (texture(uShadow, vec3(s.xy + vec2(-o, -o), z)) + texture(uShadow, vec3(s.xy + vec2(o, -o), z)) +
                 texture(uShadow, vec3(s.xy + vec2(-o, o), z)) + texture(uShadow, vec3(s.xy + vec2(o, o), z)));
#else
  return 1.0;
#endif
}

/** a stage spot on p: inside its round cone, and not behind a gourd (its quarter of the shadow map) */
float spotAt(int k, vec3 p, vec3 n) {
  vec4 c = uSpotVP[k] * vec4(p + n * 0.012, 1.0);
  if (c.w < 0.0) return 0.0;
  vec3 s = c.xyz / c.w * 0.5 + 0.5;
  float cone = smoothstep(1.0, 0.72, length(s.xy * 2.0 - 1.0));
#ifdef SHADOWS
  if (cone > 0.0) {
    vec2 q = clamp(s.xy, 0.004, 0.996) * 0.5 + vec2(float(k == 1), float(k == 2)) * 0.5;
    float o = uShadowTexel, z = s.z - 0.0008;
    cone *= 0.25 * (texture(uShadow, vec3(q + vec2(-o, -o), z)) + texture(uShadow, vec3(q + vec2(o, -o), z)) +
                    texture(uShadow, vec3(q + vec2(-o, o), z)) + texture(uShadow, vec3(q + vec2(o, o), z)));
  }
#endif
  return cone;
}

float ggx(vec3 n, vec3 v, vec3 l, float rough) {
  vec3 h = normalize(v + l);
  float a = rough * rough, nh = max(dot(n, h), 0.0), d = nh * nh * (a * a - 1.0) + 1.0;
  return a * a / (PI * d * d) * 0.25 / max(dot(n, l) * dot(n, v), 0.08) * max(dot(n, l), 0.0);
}

/** cartoon a touch: soften the terminator into a broad band rather than a line */
float band(float x, float wrap) { x = (x + wrap) / (1.0 + wrap); return smoothstep(0.0, 0.55, x) * 0.85 + 0.15 * max(x, 0.0); }

/**
 * every light on a surface: the moon, the sky and ground, the porch lamp with its shadow, and the pumpkins' light
 * through their stencils (skipping \`self\`, whose own glow is done in its shader)
 */
vec3 shade(vec3 p, vec3 n, vec3 v, vec3 alb, float rough, float spec, float wrap, float back, int self) {
  vec3 col = alb * mix(uGroundAmb, uSkyAmb, n.y * 0.5 + 0.5);
  float md = dot(n, uMoonDir);
  col += uMoon * (alb * band(md, wrap) + spec * ggx(n, v, uMoonDir, rough)) + uMoon * alb * back * max(-md, 0.0);
  vec3 lv = uLampPos - p;
  float ld2 = dot(lv, lv);
  vec3 l = lv * inversesqrt(ld2);
  float behind = step(uWall + 0.01, p.z) + step(p.z, uWall + 0.01) * step(uLampPos.z, p.z);
  if (dot(uLamp, uLamp) > 0.0 && behind > 0.0) {
    float nl = dot(n, l);
    vec3 e = uLamp / (ld2 + 0.05) * lampShadow(p, n);
    col += e * (alb * band(nl, wrap) + spec * ggx(n, v, l, rough) + alb * back * max(-nl, 0.0));
  }
  for (int k = 0; k < 3; k++) {
    if (k >= uNS) break;
    vec3 sv = uSpotPos[k] - p;
    float sd2 = dot(sv, sv), lit = spotAt(k, p, n);
    if (lit <= 0.0) continue;
    vec3 sl = sv * inversesqrt(sd2);
    float nl = dot(n, sl);
    col += uSpotCol[k] * lit / sd2 * (alb * band(nl, wrap) + spec * ggx(n, v, sl, rough) + alb * back * max(-nl, 0.0));
  }
  for (int i = 0; i < MAXP; i++) {
    if (i >= uNP) break;
    if (i == self) continue;
    // out of reach: six metres for one of ordinary size, further for a giant, and faded rather than cut off
    float far = 36.0 * max(1.0, 25.0 * uPGeo[i].x * uPGeo[i].x), d2 = dot(p - uPC[i], p - uPC[i]);
    if (d2 > far) continue;
    vec3 pl;
    vec3 e = pumpkinLight(i, p, pl) * (1.0 - smoothstep(far * 0.5, far, d2));
    float nl = dot(n, pl);
    col += e * (alb * band(nl, wrap * 0.5) + spec * ggx(n, v, pl, rough) + alb * back * max(-nl, 0.0));
  }
  return col;
}

/** the mist between the eye and p: thinned by height, lit by the moon and, in closed form, by the lamp */
vec3 mist(vec3 col, vec3 p) {
  vec3 d = p - uEye;
  float D = length(d);
  vec3 dir = d / D;
  float dens = uMist.w * 0.06;
  float hfall = exp(-max(0.0, min(p.y, uEye.y)) * 0.5);
  float T = exp(-D * dens * hfall);
  vec3 o = uMist.rgb * (1.0 - T);
  // in-scattering from the lamp: the integral of 1/r^2 along the ray, atan form
  vec3 lo = uLampPos - uEye;
  float b = dot(lo, dir), h = sqrt(max(dot(lo, lo) - b * b, 1e-4));
  float glow = (atan((D - b) / h) + atan(b / h)) / h;
  o += uLamp * glow * dens * 0.04;
  for (int i = 0; i < MAXP; i++) {
    if (i >= uNP) break;
    vec3 po = uPC[i] - uEye;
    float pb = dot(po, dir), ph = sqrt(max(dot(po, po) - pb * pb, 1e-4));
    o += uPL[i] * ((atan((D - pb) / ph) + atan(pb / ph)) / ph) * dens * 0.006;
  }
  return col * T + o;
}
`;

// ---- props: the porch, the stems, the candles

export const PROP_VS = /* glsl */`
layout(location = 0) in vec3 aPos;
layout(location = 1) in vec3 aNormal;
layout(location = 2) in vec3 aColor;
layout(location = 3) in float aMat;
uniform mat4 uVP, uModel;
out vec3 vW, vN, vC, vL;
flat out int vMat;
void main() {
  vec4 w = uModel * vec4(aPos, 1.0);
  vW = w.xyz; vL = aPos;
  vN = mat3(uModel) * aNormal;
  vC = aColor; vMat = int(aMat + 0.5);
  gl_Position = uVP * w;
}`;

export const PROP_FS = /* glsl */`
${COMMON}
in vec3 vW, vN, vC, vL;
flat in int vMat;
uniform vec3 uEmit;
uniform int uSelf;
out vec4 frag;
void main() {
  if (uUnder > 0.5 && vW.y > 0.0) discard;  // the mirror image of a gourd sinking through the stage
  vec3 n = normalize(vN);
  if (!gl_FrontFacing) n = -n;
  vec3 v = normalize(uEye - vW);
  vec3 alb = vC;
  float rough = 0.75, spec = 0.02, wrap = 0.2, back = 0.0;
  vec3 emit = vec3(0.0);
  if (vMat == 1) {          // planks: grain along z, knots, worn paint at the middle
    float g = fbm(vec2(vL.x * 60.0, vL.z * 3.0)) * 0.5 + 0.5 * vnoise(vec2(vL.x * 140.0, vL.z * 9.0));
    alb *= 0.7 + 0.5 * g;
    alb *= 1.0 - 0.35 * smoothstep(0.75, 0.95, vnoise(vL.xz * vec2(9.0, 2.0)));
    rough = 0.85;
  } else if (vMat == 18 || vMat == 19) {   // railings: painted wood, grain along the length (18 runs along x, 19 up), worn through to bare board
    vec2 gp = vMat == 18 ? vec2((vL.y + vL.z) * 90.0, vL.x * 3.5) : vec2((vL.x + vL.z) * 90.0, vL.y * 3.5);
    float ring = mix(0.5, sin(gp.x * 7.0 + vnoise(gp * vec2(0.12, 0.9)) * 9.0) * 0.5 + 0.5, clamp(1.5 - 2.5 * fwidth(gp.x), 0.0, 1.0));   // growth lines, wandering along the board
    // the noise runs coarser across the board than the growth lines: at 90 it went finer than a pixel and shredded
    float g = ring * 0.45 + fbm(gp * vec2(0.3, 1.0)) * 0.35 + 0.2 * vnoise(gp * vec2(0.7, 2.8) + 11.0);
    vec2 fwg = fwidth(gp);
    float gk = clamp(1.6 - 1.5 * fwg.x, 0.0, 1.0);           // fade the grain to its mean where it is finer than a pixel
    float hgt = mix(0.5, g, gk);
    float knot = smoothstep(0.86, 0.96, vnoise(gp * vec2(0.18, 0.5) + 5.0));
    float wearR = smoothstep(0.52, 0.72, fbm(gp * vec2(0.5, 0.6) + 21.0)) * smoothstep(0.35, 0.8, hgt);
    vec3 bare = vec3(0.19, 0.145, 0.095) * (0.65 + 0.7 * hgt);
    alb *= 0.4 + 1.0 * hgt;
    alb = mix(alb, bare, wearR * 0.7);
    alb *= 1.0 - 0.5 * knot;
    // bump from the grain's height, as on the skin: the lit side of every ridge catches the light. Only while a ridge
    // spans several pixels: derivatives taken per 2x2 block turned finer grain into a checker
    vec3 sx = dFdx(vW), sy = dFdy(vW);
    vec3 r1 = cross(sy, n), r2 = cross(n, sx);
    float det = dot(sx, r1);
    vec3 grad = sign(det) * (dFdx(hgt) * r1 + dFdy(hgt) * r2) * 0.006 * clamp(1.2 - 3.0 * fwg.x, 0.0, 1.0);
    if (abs(det) > 0.0) n = normalize(abs(det) * n - grad);
    rough = 0.8; spec = 0.035;
  } else if (vMat == 2) {   // siding: lap boards, grain, butt joints, knots, drips and worn paint
    float bi = floor((vL.y - 0.45) / 0.153), fy = fract((vL.y - 0.45) / 0.153);
    float hb = hash12(vec2(bi, 3.7));
    float grain = fbm(vec2(vL.x * 2.2 + hb * 40.0, vL.y * 110.0));
    alb *= 4.2 * (0.72 + 0.5 * hb) * (0.7 + 0.55 * grain);
    float jf = abs(fract(vL.x * 0.42 + hb * 5.0) - 0.5);
    alb *= 1.0 - 0.7 * smoothstep(0.488, 0.5, jf);
    alb *= 1.0 - 0.8 * smoothstep(0.8, 0.9, vnoise(vec2(vL.x * 6.0 + hb * 20.0, vL.y * 34.0)));
    alb *= 1.0 - 0.45 * smoothstep(0.88, 1.0, fy) - 0.3 * smoothstep(0.1, 0.0, fy) * (1.0 - hb * 0.5);
    float drip = fbm(vec2(vL.x * 16.0 + bi, vL.y * 1.1));
    alb *= 0.8 + 0.35 * drip;
    alb = mix(alb, alb * vec3(0.7, 0.95, 0.75), 0.4 * smoothstep(0.5, 0.9, drip) * smoothstep(1.3, 0.45, vL.y));
    float wear = smoothstep(0.64, 0.8, vnoise(vec2(vL.x * 5.0, vL.y * 55.0) + hb * 9.0)) * (0.35 + 0.65 * smoothstep(0.5, 0.0, fy));
    alb = mix(alb, vec3(0.2, 0.17, 0.13) * (0.5 + grain), wear * 0.75);
    n = normalize(n + vec3(0.0, -0.9 * smoothstep(0.07, 0.0, fy), 0.25 * (grain - 0.5)));
    rough = 0.85 - 0.1 * (1.0 - wear);
  } else if (vMat == 3) {   // door: glossy enamel, brush grain, scuffed at the foot
    float gr = fbm(vec2(vL.x * 30.0, vL.y * 3.0));
    alb *= 0.82 + 0.3 * gr;
    float scuff = smoothstep(0.85, 0.45, vL.y) * smoothstep(0.35, 0.8, fbm(vL.xy * 22.0));
    alb = mix(alb, alb * 0.55 + vec3(0.03), scuff);
    alb *= 1.0 - 0.5 * smoothstep(0.86, 0.93, vnoise(vL.xy * 11.0));
    rough = 0.3; spec = 0.12;
  } else if (vMat == 4) {   // plastic: the pail and the sweets, with a painted face on the pail
    rough = 0.3; spec = 0.06;
    vec2 q = vec2(atan(vL.x - ${1.35}, vL.z + ${0.55}), vL.y - ${0.45});
    if (vC.r > 0.7 && vC.g < 0.2) {
      float face = step(abs(q.x - 1.57), 1.0);
      float eyes = step(length(vec2(abs(q.x - 1.57) - 0.38, (q.y - 0.12) * 3.2)), 0.12);
      float mouth = step(abs(q.x - 1.57), 0.6) * step(abs(q.y - 0.055 - 0.012 * sin(q.x * 18.0)), 0.014);
      alb = mix(alb, vec3(0.01), face * max(eyes, mouth));
    }
  } else if (vMat == 5) {   // stem: fibrous
    alb *= 0.75 + 0.5 * vnoise(vec2(atan(vL.x, vL.z) * 8.0, vL.y * 30.0));
    rough = 0.8;
  } else if (vMat == 6) {   // wax: lit through
    back = 0.6; wrap = 0.8; rough = 0.5; spec = 0.03;
  } else if (vMat == 7) {   // metal
    rough = 0.4; spec = 0.25; alb *= 0.6;
    alb *= 0.8 + 0.4 * vnoise(vL.xy * 90.0 + vL.z * 60.0);
  } else if (vMat == 8) {   // the window: lamplight behind curtains
    float curtain = smoothstep(0.1, 0.0, abs(fract(vL.x * 1.8 + 0.25) - 0.5) - 0.32);
    float folds = 0.75 + 0.25 * sin(vL.x * 60.0 + sin(vL.y * 3.0) * 2.0);
    emit = vC * vec3(0.42, 0.36, 0.3) * mix(1.0, 0.3 * folds, curtain) * (0.9 + 0.1 * sin(uTime * 1.3));
    alb = vec3(0.01);
  } else if (vMat == 9) {   // soil: dark under the grass
    alb *= 0.6 + 0.6 * fbm(vL.xz * 2.0);
  } else if (vMat == 10) {  // leaves and bushes
    rough = 0.7; back = 0.35; wrap = 0.4;
    alb *= 0.7 + 0.5 * vnoise(vL.xz * 30.0 + vL.y * 20.0);
  } else if (vMat == 11) {  // stone
    alb *= 0.7 + 0.5 * fbm(vL.xz * 12.0);
    rough = 0.9;
  } else if (vMat == 12) {  // glowing: the lamp's glass, an LED
    emit = vC * uEmit;
  } else if (vMat == 13) {  // shingles
    alb *= 0.6 + 0.6 * step(0.1, fract(vL.x * 3.0 + floor(vL.z * 6.0) * 0.5));
  } else if (vMat == 15) {  // brass: warm, polished, tarnished in the creases
    rough = 0.22; spec = 0.8;
    alb *= 0.8 + 0.35 * vnoise(vL.xy * 70.0 + vL.z * 50.0);
    emit = alb * uSkyAmb * 14.0 * (0.3 + pow(1.0 - max(dot(n, v), 0.0), 2.0));
  } else if (vMat == 16) {  // glass: mostly see-through (a dither), dense at the rim
    float fr = pow(1.0 - abs(dot(n, v)), 2.0);
    float dither = fract(dot(floor(gl_FragCoord.xy), vec2(0.7548776662, 0.5698402910)));
    if (dither > 0.1 + 0.8 * fr) discard;
    bool bulb = vC.r > vC.b + 0.2;
    emit = vC * uEmit * (bulb ? 0.45 : 0.06 + 0.4 * fr);
    alb = vec3(0.01); rough = 0.08; spec = 0.8;
  } else if (vMat == 17) {  // the filament: white-hot
    emit = vC * uEmit * 3.2;
    alb = vec3(0.0);
  } else if (vMat == 20) {  // the stage: polished granite, black with grey and white grains, going matte up the sweep
    float fl = smoothstep(0.5, 0.0, vW.y);
    vec2 gp = vW.xz * 420.0;
    float g1 = vnoise(gp), g2 = vnoise(gp * 0.61 + 11.0), cl = fbm(vW.xz * 2.5);
    vec3 grain = vec3(0.04, 0.04, 0.043) + vec3(0.06, 0.058, 0.055) * smoothstep(0.55, 0.8, g1) + vec3(0.16) * smoothstep(0.88, 0.95, g2);
    grain = mix(vec3(0.062), grain, keep(vec2(420.0), fwidth(vW.xz)));
    alb = mix(alb, grain * (0.8 + 0.4 * cl), fl);
    // gloss only in front of the sweep: at the back, seen at a graze, the left spot made a white streak there
    float gl = smoothstep(-1.5, -0.5, vW.z);
    rough = mix(0.8, 0.17 + 0.08 * cl, gl); spec = mix(0.02, 0.3, gl);
  }
  vec3 col = emit + shade(vW, n, v, alb, rough, spec, wrap, back, uSelf);
  frag = finish(mist(col, vW));
}`;

// ---- the pumpkin's skin, carved by its stencil

export const PUMPKIN_VS = /* glsl */`
layout(location = 0) in vec3 aPos;
layout(location = 1) in vec3 aNormal;
layout(location = 2) in vec2 aUv;
layout(location = 3) in float aGroove;
layout(location = 4) in float aWart;
layout(location = 5) in vec3 aSheet;   // where the carving is looked up: the outer skin, a gourd's neck unbent
uniform mat4 uVP, uModel;
uniform float uInset;
// centroid: with MSAA an edge pixel's centre can sit outside a thin triangle, and a value extrapolated from there
// (a groove of 3, a point half a metre from the candle) turns the wall's exp into infinity
centroid out vec3 vW, vL, vN, vNl;
centroid out vec3 vO;   // the sheet point of the outer skin this one was pulled in from
centroid out vec2 vUv;
centroid out float vGroove, vWart;
void main() {
  vec3 p = aPos - aNormal * uInset;
  vec4 w = uModel * vec4(p, 1.0);
  vW = w.xyz; vL = p; vO = aSheet; vNl = aNormal;
  vN = mat3(uModel) * aNormal;
  vUv = aUv; vGroove = aGroove; vWart = aWart;
  gl_Position = uVP * w;
}`;

export const PUMPKIN_FS = /* glsl */`
${COMMON}
centroid in vec3 vW, vL, vN, vNl;
centroid in vec3 vO;
centroid in vec2 vUv;
centroid in float vGroove, vWart;
uniform int uSelf;
uniform vec3 uSkin;
uniform vec3 uBase2;    // second skin colour
uniform vec4 uGrooveCol; // crease colour, w = 1 when set
uniform vec4 uPat;      // speckle, stripes, bicolor split (share of height), mottle
uniform vec4 uPat2;     // stripe count, marble, the stencil's ink shown, its dotted outline shown (each on its own)
uniform vec4 uLook;     // rib tint, wall (m), kind, seed
out vec4 frag;

float height(vec2 uv, vec2 fw) {
  // fine striations running stem to base, broken up and wandering; shallow pocks; a fine pebbling over all of it
  float ribs = sin(uv.x * PI * 2.0 * 46.0 + vnoise(uv * vec2(40.0, 6.0)) * 3.0) * 0.5 + 0.5;
  float fine = sin(uv.x * PI * 2.0 * 118.0 + vnoise(uv * vec2(90.0, 9.0)) * 5.0) * 0.5 + 0.5;
  fine *= 0.6 + 0.4 * vnoise(uv * vec2(30.0, 14.0));
  float pock = smoothstep(0.55, 0.85, vnoise(uv * vec2(70.0, 34.0) + 4.0));
  float a = keep(vec2(46.0, 6.0), fw), f = keep(vec2(118.0, 9.0), fw), c = keep(vec2(70.0, 34.0), fw), b = keep(vec2(180.0, 70.0), fw);
  return mix(0.5, ribs, a) * 0.35 + mix(0.5, fine, f) * 0.25 + mix(0.0, -pock, c) * 0.3 + mix(0.5, vnoise(uv * vec2(180.0, 70.0)), b) * 0.3;
}

void main() {
  if (uUnder > 0.5 && vW.y > 0.0) discard;  // the mirror image of a gourd sinking through the stage
  int me = uSelf;
  float sq = uPGeo[me].y;
  vec3 light = uPL[me];
  vec2 sheet = sheetOf(vO, sq);
  vec3 m = maskAt(me, sheet, 0.0);
  vec3 vw = normalize(vW - uEye);
  vec3 nl = normalize(vNl);
  float wall = uLook.y;

  // the skin seen from inside: the inner wall and the cut faces (CUT_FS) cover it
  if (!gl_FrontFacing) discard;

  // a hole: nothing here. Its cut face is a strip of its own, stood on the edge (cutwall.ts, CUT_FS). With MSAA the
  // edge takes the share of the pixel it covers (alpha to coverage), so it doesn't stair-step
  float cover = clamp(0.5 + (0.5 - m.r) / max(fwidth(m.r), 1e-4), 0.0, 1.0);
#ifdef A2C
  if (cover <= 0.0 || m.g > 0.97) discard;
#else
  if (m.r > 0.5 || m.g > 0.97) discard;
#endif

  // the skin
  vec3 n = normalize(vN);
  if (!gl_FrontFacing) n = -n;
  vec2 fw = fwidth(vUv);
  float h = height(vUv, fw);
#ifdef LDR
  float hx = dFdx(h), hy = dFdy(h);
#else
  // the height's slope in uv by finite differences, carried to the screen through the uv's derivatives: dFdx(h) itself
  // is differenced across a 2x2 quad and turned fine relief into a dithered checker. The raster tier keeps the cheap one
  const float E = 0.0004;
  vec2 dh = (vec2(height(vUv + vec2(E, 0.0), fw), height(vUv + vec2(0.0, E), fw)) - h) / E;
  float hx = dot(dh, dFdx(vUv)), hy = dot(dh, dFdy(vUv));
#endif
  vec3 sx = dFdx(vW), sy = dFdy(vW);
  vec3 r1 = cross(sy, n), r2 = cross(n, sx);
  float det = dot(sx, r1);
  // bump from screen derivatives (Mikkelsen): both terms scale with the pixel's footprint, so the tilt is the height's
  // slope in metres; h runs 0..1 over about 3 mm of relief
  vec3 grad = sign(det) * (hx * r1 + hy * r2) * 0.0032;
  // no more than ~30 degrees of tilt: past that, grazing pixels flipped to black speckle
  float gl = length(grad), lim = 0.55 * abs(det);
  if (gl > lim) grad *= lim / gl;
  // and none at the silhouette, where the 2x2 derivatives straddle huge depth steps and gnaw the outline
  grad *= smoothstep(0.08, 0.4, abs(dot(n, vw)));
  if (abs(det) > 0.0) n = normalize(abs(det) * n - grad);

  // skin: colours first (bicolor, mottle, marbling, stripes, flecks), then the field's streaks and the creases
  vec3 w3 = pow(abs(nl), vec3(4.0)); w3 /= (w3.x + w3.y + w3.z);
  float fwL = max(fwidth(vL.x), max(fwidth(vL.y), fwidth(vL.z)));
  #define TN(p) (vnoise((p).zy) * w3.x + vnoise((p).xz + 3.1) * w3.y + vnoise((p).xy + 7.7) * w3.z)
  vec3 alb = uSkin;
  if (uPat.z > 0.0) {
    float edge = uPat.z + 0.07 * (TN(vL * 16.0) - 0.5) * 2.0 + 0.025 * sin(vUv.x * PI * 2.0 * 3.0 + uLook.w);
    alb = mix(alb, uBase2, smoothstep(edge - 0.035, edge + 0.035, vUv.y));
  }
  if (uPat.w > 0.0) {
    float lowN = TN(vL * 12.0) * 0.65 + TN(vL * 27.0) * 0.35;
    alb = mix(alb, uBase2, smoothstep(0.42, 0.68, lowN) * uPat.w * keep(vec2(27.0), vec2(fwL)));
  }
  if (uPat.x > 0.0) {
    float fleck = smoothstep(0.64 - 0.06 * uPat.x, 0.7 - 0.06 * uPat.x, TN(vL * 190.0));
    alb = mix(alb, uBase2 * 1.4, fleck * uPat.x * keep(vec2(190.0), vec2(fwL)));
  }
  if (uPat2.y > 0.0) {
    float marble = smoothstep(0.5, 0.6, TN(vL * 30.0) * 0.7 + TN(vL * 63.0) * 0.3);
    alb = mix(alb, uBase2, marble * 0.85 * uPat2.y * keep(vec2(63.0), vec2(fwL)));
  }
  if (uPat.y > 0.0) {
    float cnt = max(uPat2.x, 2.0);
    float ph = vUv.x * cnt + 0.3 * (TN(vL * 24.0) - 0.5) + 0.08 * sin(vUv.y * 9.0 + uLook.w);
    float band = 1.0 - smoothstep(0.17, 0.3, abs(fract(ph) - 0.5));
    alb = mix(alb, uBase2, mix(0.45, band, keep(vec2(cnt, 0.0), vec2(fw.x, 0.0))) * uPat.y);
  }
  float streak = mix(0.5, vnoise(vec2(vUv.x * 300.0, vUv.y * 8.0)), keep(vec2(300.0, 8.0), fw));
  alb *= 0.82 + 0.3 * streak;
  vec3 deep = uGrooveCol.w > 0.5 ? uGrooveCol.rgb : uSkin * vec3(0.55, 0.75, 0.35) + vec3(0.0, 0.015, 0.0);
  alb = mix(alb, deep * (0.85 + 0.3 * streak), clamp(vGroove * uLook.x * 1.4, 0.0, 1.0));
  alb = mix(alb, alb * vec3(0.9, 1.15, 0.6) + vec3(0.03, 0.04, 0.0), vWart * 0.6);
  if (uLook.z == 9.0 && vUv.y < 0.34) alb = mix(alb, vec3(0.35, 0.4, 0.2), 0.5 * step(0.5, fract(vUv.x * 20.0)));
  alb *= 0.85 + 0.25 * smoothstep(0.85, 0.4, vUv.y);   // paler underneath where it sat in the field
  alb = mix(alb, alb * vec3(1.0, 0.9, 0.6), smoothstep(0.82, 1.0, vUv.y));
  // not plastic: a waxy sheen in patches, dull bloom between, so no highlight runs smooth across the skin
  float blot = fbm(vUv * vec2(16.0, 7.0) + 3.7);
  float rough = 0.46 + 0.2 * streak + 0.2 * blot;
  float spec = 0.04 * smoothstep(0.75, 0.3, blot) + 0.008;

  // the stencil traced on the skin: black ink over the area and a firmer line at its edge; or, carving with the tools,
  // only its outline dashed (traced and dashed on the CPU, uDash, so the dashes run along the line and stay put)
  // two shades, as on the paper: the parts to scrape are pale (blue at half), the parts to cut black (blue full)
  float inkA = smoothstep(0.2, 0.42, m.b) * uPat2.z;
  float ink = smoothstep(0.62, 0.88, m.b) * uPat2.z;
  float wash = inkA * (1.0 - ink);
  float edgeL = smoothstep(0.06, 0.3, m.b) * smoothstep(0.48, 0.38, m.b) + smoothstep(0.55, 0.7, m.b) * smoothstep(0.98, 0.85, m.b);
  float edge = edgeL * uPat2.z;
  float dots = 0.0;
  if (uPat2.w > 0.0 && abs(sheet.x) < 1.0 && abs(sheet.y) < 1.0) dots = texture(uDash, vec2(sheet.x * 0.5 + 0.5, 0.5 - sheet.y * 0.5)).r * (1.0 - ink);
  alb *= 1.0 - 0.985 * ink - 0.15 * edge;
  alb = mix(alb, vec3(0.2, 0.19, 0.18), 0.62 * wash);
  alb = mix(alb, vec3(0.004), clamp(0.9 * edge + dots, 0.0, 1.0));
  // scraped: the rind shaved off and the flesh showing. The first layer is bright, wet and fibrous, the fibres running
  // up the ribs; deeper it goes paler and nearer the light. The rind stands up round it as a pale lip
  float shaved = smoothstep(0.0, 0.2, m.g);
  vec2 fwU = fwidth(vUv);
  float fib = vnoise(vec2(vUv.x * 520.0, vUv.y * 22.0)) * keep(vec2(520.0, 22.0), fwU);
  float pore = smoothstep(0.72, 0.8, vnoise(vUv * vec2(260.0, 140.0) + 5.3)) * keep(vec2(260.0, 140.0), fwU);
  vec3 flesh = mix(vec3(1.0, 0.62, 0.14), vec3(1.0, 0.78, 0.42), smoothstep(0.25, 0.85, m.g));
  flesh *= (0.8 + 0.32 * fib) * (1.0 - 0.25 * pore);
  // the edge of a scrape, from the blurred mask: outside it the rind stands up as a lit lip, inside it drops in shadow
  float lip = 0.0, step_ = 0.0;
  {
    float gB = textureLod(uMasks, vec3(carveUV(sheet), float(uLayer[me])), log2(float(textureSize(uMasks, 0).y) / (PI * 200.0))).g;
    lip = (1.0 - shaved) * smoothstep(0.02, 0.3, gB);
    step_ = shaved * smoothstep(0.75, 0.3, gB / max(m.g, 0.05));
  }
  alb = mix(alb, flesh, shaved);
  alb *= (1.0 + 0.3 * lip) * (1.0 - 0.45 * step_);
  rough = mix(rough, 0.55 - 0.15 * fib, shaved);
  spec = mix(spec, 0.03, shaved) + 0.06 * lip;

  vec3 col = shade(vW, n, -vw, alb, rough, spec, 0.25, 0.0, me);
  col *= (1.0 - 0.9 * ink) * (1.0 - 0.85 * dots);

  // the light inside, through the wall: one exp of the wall's thickness, thinner in the grooves and where scraped;
  // more round the holes, where the flesh is lit at its cut edge
  vec3 toSrc = uPSrc[me] - vL;
  float d2 = dot(toSrc, toSrc);
  float thick = wall * (1.0 - 0.35 * clamp(vGroove, 0.0, 1.0)) * (1.0 - 0.1 * shaved - 0.84 * m.g * m.g);   // a shallow scrape shows flesh before it lets light through
  float T = exp(-thick * 190.0) * uPGeo[me].w;
  float near = textureLod(uMasks, vec3(carveUV(sheet), float(uLayer[me])), log2(float(textureSize(uMasks, 0).y) / (PI * 64.0))).r;
  // through the rind the light takes the rind's own colour (green through green, cream through white), and only a
  // little gets through; scraped down to the flesh, and round the holes, it is the flesh's warm orange
  vec3 warm = mix(vec3(1.0, 0.45, 0.08), vec3(1.0, 0.62, 0.25), m.g);
  float top = max(max(uSkin.r, uSkin.g), max(uSkin.b, 0.001));
  vec3 rind = mix(warm, uSkin / top * vec3(1.0, 0.84, 0.72), 0.7) * 0.55 * sqrt(top);   // a dark rind lets less through
  float sc = clamp(max(shaved, m.g * 1.5), 0.0, 1.0);
  vec3 glow = light / (d2 + 0.002) * (T * mix(rind, warm, sc) + near * 0.012 * warm);
  col += glow * (0.5 + 0.5 * max(0.0, dot(nl, normalize(-toSrc)))) * (1.0 - 0.97 * max(max(ink, dots), 0.6 * wash)) * (1.0 - 0.5 * step_);   // ink is opaque to the light behind it

  frag = finish(mist(col, vW));
  frag.a = cover;
}`;

/** the cut face of the flesh: a strip on each edge of the carving, from the skin in to the inner wall (cutwall.ts) */
export const CUT_VS = /* glsl */`
layout(location = 0) in vec3 aPos;
layout(location = 1) in vec3 aNormal;
layout(location = 2) in float aIn;
uniform mat4 uVP, uModel;
centroid out vec3 vW, vL, vN, vNl;
centroid out float vIn;
void main() {
  vec4 w = uModel * vec4(aPos, 1.0);
  vW = w.xyz; vL = aPos; vNl = aNormal; vN = mat3(uModel) * aNormal; vIn = aIn;
  gl_Position = uVP * w;
}`;

export const CUT_FS = /* glsl */`
${COMMON}
centroid in vec3 vW, vL, vN, vNl;
centroid in float vIn;
uniform int uSelf;
uniform vec3 uSkin;
out vec4 frag;
void main() {
  if (uUnder > 0.5 && vW.y > 0.0) discard;  // the mirror image of a gourd sinking through the stage
  int me = uSelf;
  float t = clamp(vIn, 0.0, 1.0), R = uPGeo[me].x;
  vec3 vw = normalize(vW - uEye);
  vec3 n = normalize(vN), nl = normalize(vNl);
  if (dot(n, vw) > 0.0) { n = -n; nl = -nl; }
  // fresh flesh: pale and yellow through the middle, deeper orange toward the strings inside, a thin rind under the
  // skin. The knife leaves it smooth but for the grain, which runs in through the wall
  float grain = vnoise(vec2(t * 7.0, dot(vL, vec3(61.0, 57.0, 67.0))));
  float fleck = vnoise(vec2(t * 30.0, dot(vL, vec3(173.0, 181.0, 167.0))));
  vec3 alb = mix(vec3(1.0, 0.56, 0.1), vec3(0.95, 0.4, 0.04), smoothstep(0.45, 1.0, t));
  alb *= 0.86 + 0.18 * grain - 0.1 * fleck * smoothstep(0.6, 1.0, t);
  alb = mix(uSkin * 0.75, alb, smoothstep(0.03, 0.09, t));
  // the candle on the face itself, and through the flesh: it is lit from the inside, brighter the nearer the hollow
  vec3 toSrc = uPSrc[me] - vL;
  float d2 = dot(toSrc, toSrc);
  vec3 lit = uPL[me] / (d2 + R * R * 0.3);
  float face = max(0.0, dot(nl, normalize(toSrc)));
  vec3 col = alb * lit * (0.1 * face + 0.03 + 0.14 * t * t);
  // and the scene's light, with the wet shine of a fresh cut
  col += shade(vW, n, -vw, alb * 0.6, 0.5, 0.1, 0.4, 0.0, me);
  frag = finish(mist(col, vW));
}`;

/** the inside, drawn as the back faces of the shell pulled in by the wall's thickness */
export const INSIDE_FS = /* glsl */`
${COMMON}
centroid in vec3 vW, vL, vN, vNl;
centroid in vec2 vUv;
centroid in vec3 vO;
centroid in float vGroove, vWart;
uniform int uSelf;
out vec4 frag;
void main() {
  if (uUnder > 0.5 && vW.y > 0.0) discard;  // the mirror image of a gourd sinking through the stage
  int me = uSelf;
  // the carving by the outer skin's point, not this one's: pulled in along the normal, which on a squat top points well
  // off the centre, its own direction found a different latitude and opened the inner wall where the skin was whole
  vec3 cut = maskAt(me, sheetOf(vO, uPGeo[me].y), 0.0);
  if (cut.r > 0.5 || cut.g > 0.97) discard;   // cut through: no inner wall here either; the skin pass draws the cut face
  vec3 n = -normalize(vNl);
  vec3 toSrc = uPSrc[me] - vL;
  float d2 = dot(toSrc, toSrc);
  float R = uPGeo[me].x;
  float pulp = vnoise(vec2(vUv.x * 90.0, vUv.y * 14.0 + vnoise(vUv * 30.0) * 3.0));
  vec3 alb = mix(vec3(0.85, 0.42, 0.08), vec3(1.0, 0.7, 0.3), pulp);
  alb *= 0.75 + 0.35 * vnoise(vUv * vec2(240.0, 60.0));
  // scorched over the flame
  alb *= 1.0 - 0.7 * smoothstep(0.3, 0.0, vUv.y) * step(0.0, uPL[me].r - uPL[me].b * 2.0);
  float nl = max(0.0, dot(n, normalize(toSrc)));
  // a flame is a few centimetres of light, not a point: the floor beside it is bright, not a sun
  vec3 light = uPL[me] / (d2 + R * R * 0.3);
  vec3 col = alb * light * (nl * 0.65 + 0.2) + alb * uPL[me] / (R * R * 3.0) * 0.18;
  frag = finish(mist(col, vW));
}`;

// ---- flames and LEDs: camera-facing sprites drawn additively after everything solid

export const FLAME_VS = /* glsl */`
layout(location = 0) in vec4 aAt;     // world xyz, size
layout(location = 1) in vec4 aCol;    // colour times power, kind (0 flame, 1 led)
uniform mat4 uVP;
uniform vec3 uRight, uUp;
uniform float uTime;
out vec2 vQ;
out vec4 vCol;
void main() {
  vec2 c = vec2(float(gl_VertexID & 1), float((gl_VertexID >> 1) & 1)) * 2.0 - 1.0;
  float flame = 1.0 - aCol.w;
  float sway = flame * (sin(uTime * 7.3 + aAt.x * 40.0) * 0.12 + sin(uTime * 13.1 + aAt.z * 30.0) * 0.06) * max(0.0, c.y);
  vec3 p = aAt.xyz + uRight * (c.x + sway) * aAt.w + uUp * (c.y * mix(1.0, 2.2, flame) + flame * 1.1) * aAt.w;
  vQ = c; vCol = aCol;
  gl_Position = uVP * vec4(p, 1.0);
}`;

export const FLAME_FS = /* glsl */`
uniform float uExposure;
in vec2 vQ;
in vec4 vCol;
out vec4 frag;
void main() {
  float a;
  vec3 c;
  if (vCol.w < 0.5) {
    // a teardrop: round at the foot, drawn up to a point; white-blue core, orange skin
    vec2 q = vec2(vQ.x / (0.55 * (1.0 - smoothstep(-0.6, 1.0, vQ.y)) + 0.05), vQ.y + 0.35);
    float d = length(vec2(q.x, q.y * 0.65));
    a = smoothstep(1.0, 0.2, d);
    c = mix(vec3(1.0, 0.35, 0.05), vec3(1.0, 0.85, 0.55), smoothstep(0.75, 0.2, d)) * a;
    c += vec3(0.2, 0.3, 1.0) * smoothstep(0.5, 0.0, length(vec2(q.x * 1.6, q.y + 0.7))) * 0.6;
    c *= length(vCol.rgb) * 0.06;
  } else {
    float d = length(vQ);
    a = smoothstep(1.0, 0.0, d);
    c = vCol.rgb * 0.05 * (a * a + 0.6 * smoothstep(0.3, 0.0, d));
  }
#ifdef LDR
  c = 1.0 - exp(-c * uExposure);
#endif
  frag = vec4(c, 0.0);
}`;

// ---- shadow depth

export const DEPTH_VS = /* glsl */`
layout(location = 0) in vec3 aPos;
uniform mat4 uVP, uModel;
void main() { gl_Position = uVP * uModel * vec4(aPos, 1.0); }`;
export const DEPTH_FS = /* glsl */`out vec4 frag; void main() { frag = vec4(1.0); }`;
// a pumpkin's skin casts only where it is still there: a lid cut away, or a face, lets the light through
export const DEPTH_CUT_VS = /* glsl */`
layout(location = 0) in vec3 aPos;
layout(location = 5) in vec3 aSheet;
uniform mat4 uVP, uModel;
centroid out vec3 vO;
void main() { vO = aSheet; gl_Position = uVP * uModel * vec4(aPos, 1.0); }`;
export const DEPTH_CUT_FS = /* glsl */`
#define PI 3.14159265
#define SHEET_PHI 1.25
#define SHEET_THETA 1.0
uniform sampler2DArray uMasks;
uniform int uSelf;
uniform float uSquash;
centroid in vec3 vO;
out vec4 frag;
void main() {
  vec2 sheet = vec2(atan(vO.x, vO.z) / SHEET_PHI, atan(vO.y / uSquash, length(vO.xz)) / SHEET_THETA);
  vec2 g = textureLod(uMasks, vec3(sheet.x * SHEET_PHI / (2.0 * PI) + 0.5, 0.5 - sheet.y * SHEET_THETA / PI, float(uSelf)), 0.0).rg;
  if (g.r > 0.5 || g.g > 0.97) discard;
  frag = vec4(1.0);
}`;

// ---- the night

export const SKY_FS = /* glsl */`
${COMMON}
in vec2 vUv;
uniform mat4 uInvVP;
out vec4 frag;
void main() {
  vec4 a = uInvVP * vec4(vUv * 2.0 - 1.0, 1.0, 1.0);
  vec3 d = normalize(a.xyz / a.w - uEye);
  float y = d.y;
  vec3 col = mix(vec3(0.012, 0.014, 0.03), vec3(0.002, 0.003, 0.012), smoothstep(0.0, 0.6, y));
  col += vec3(0.03, 0.02, 0.035) * exp(-max(y, 0.0) * 9.0);
  // stars
  vec2 sp = vec2(atan(d.x, d.z), y) * 220.0;
  float st = hash12(floor(sp));
  col += vec3(0.6, 0.7, 1.0) * step(0.996, st) * smoothstep(0.02, 0.3, y) * (0.4 + 0.6 * sin(uTime * 2.0 + st * 80.0)) * 0.2;
  // the moon and its halo
  float md = max(dot(d, uMoonDir), 0.0);
  col += uMoon * (smoothstep(0.9993, 0.9996, md) * 60.0 + pow(md, 200.0) * 1.2 + pow(md, 12.0) * 0.12);
  // clouds lit by it
  vec2 cp = d.xz / max(y, 0.05) * 0.6 + vec2(uTime * 0.01, 0.0);
  float cl = smoothstep(0.45, 0.8, fbm(cp)) * smoothstep(0.02, 0.25, y);
  col = mix(col, uMoon * 0.25 * (0.6 + pow(md, 6.0)), cl * 0.7);
  // the tree line, black against it all
  float az = atan(d.x, d.z);
  float trees = 0.06 + 0.07 * fbm(vec2(az * 3.0, 0.0)) + 0.1 * pow(fbm(vec2(az * 14.0, 1.0)), 2.0) + 0.05 * step(0.8, vnoise(vec2(az * 30.0, 2.0)));
  col = mix(col, vec3(0.002, 0.003, 0.004), smoothstep(trees + 0.004, trees - 0.004, y));
  frag = finish(mist(col, uEye + d * 40.0));
}`;

// ---- grass, from liminality's render/grass.ts: world-fixed cells, a blade made from its cell and index by PCG, a
// quadratic curve that keeps its length as it leans, tapered (1 - v^1.6), lit cheaply. The lean pass is per vertex here.

export const GRASS_VS = /* glsl */`
${COMMON}
uniform mat4 uVP;
uniform highp isampler2D uCells;
uniform ivec4 uBand;          // first cell in the list, blades a cell, segments, seed
uniform vec4 uLook;           // density, height, width, dryness
uniform vec4 uMore;           // radius, pixels, hz, pixel (m a pixel a metre away)
uniform vec4 uVary;           // patch, variety, clumps, wind strength
uniform vec4 uBare[4];
uniform vec2 uDrift;
out vec3 vW, vN, vAlb;
out float vT;
const float CELL = 2.0;

float grNext(inout uint s) { return float(pcg(s) >> 8u) / 16777216.0; }
uint grHash(uint a, uint b, uint c) {
  uint h = (a * 0x8da6b343u) ^ (b * 0xd8163841u) ^ (c * 0xcb1ab31fu);
  h = (h ^ (h >> 16u)) * 0x7feb352du; h = (h ^ (h >> 15u)) * 0x846ca68bu;
  return h ^ (h >> 16u);
}
float grU(int x, int y, uint k) { return float(grHash(uint(x), uint(y), k) >> 8u) / 16777216.0; }
float grNoise(vec2 p, uint k) {
  ivec2 i = ivec2(floor(p)); vec2 f = p - floor(p); vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(grU(i.x, i.y, k), grU(i.x + 1, i.y, k), u.x), mix(grU(i.x, i.y + 1, k), grU(i.x + 1, i.y + 1, k), u.x), u.y);
}
vec3 grCurve(vec3 root, float h, vec2 l, float v) {
  float a = length(l); vec2 d = a > 1e-5 ? l / a : vec2(0.0);
  vec3 tip = vec3(d.x * sin(a), cos(a), d.y * sin(a)) * h;
  vec3 mid = vec3(d.x * sin(a * 0.35), cos(a * 0.35), d.y * sin(a * 0.35)) * h * 0.58;
  return root + 2.0 * v * (1.0 - v) * mid + v * v * tip;
}
// liminality's gusts (physics/wind.ts windGusts and windLean), without the blasts
vec2 gusts(vec2 xz, float t, int s) {
  vec2 q = (xz - uDrift * t) / 28.0; float qt = t / 9.0;
  uint us = uint(s);
  float a = 2.0 * (grNoise(q + vec2(qt * 1.3, qt * 0.7), us) * 0.65 + grNoise(q * 2.3 + vec2(5.2, 1.3) + qt * 1.7, us + 1u) * 0.35) - 1.0;
  float b = 2.0 * grNoise(q * 0.8 + vec2(11.1, 3.7) + qt, us + 2u) - 1.0;
  float k = 1.0 + 1.6 * 0.4 * a;
  return vec2(uDrift.x * k - uDrift.y * 0.2 * b, uDrift.y * k + uDrift.x * 0.2 * b);
}
vec2 windLean(vec3 p, float t, float hz, float phase, int s) {
  vec2 g = gusts(p.xz, t, s); float v = length(g);
  float bend = 1.25 * (1.0 - exp(-v * v / 49.0));
  float fl = 0.18 * bend * sin(6.2831853 * (hz * t + phase));
  float side = 0.4 * fl * sin(6.2831853 * (hz * 0.77 * t + phase * 1.9));
  vec2 u = v > 1e-6 ? g / v : vec2(1.0, 0.0);
  return u * (bend + fl) + vec2(-u.y, u.x) * side;
}

void main() {
  int vi = gl_VertexID, ii = gl_InstanceID;
  gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
  vW = vec3(0.0); vN = vec3(0.0, 1.0, 0.0); vAlb = vec3(0.0); vT = 0.0;
  int perCell = uBand.y;
  ivec2 cell = texelFetch(uCells, ivec2(uBand.x + ii / perCell, 0), 0).xy;
  uint j = uint(ii % perCell), seed = uint(uBand.w);
  uint rs = grHash(uint(cell.x), uint(cell.y), j * 4u + seed);
  vec2 base = vec2(cell) * CELL;
  vec2 at = base + vec2(grNext(rs), grNext(rs)) * CELL;
  vec2 splay = vec2(0.0); float tuft = 1.0;
  if (grNext(rs) < uVary.z) {
    uint c = 2000u + 3u * uint(grNext(rs) * 5.0) + seed * 16u;
    float r = sqrt(grNext(rs)), a = grNext(rs) * 6.2831853;
    splay = vec2(cos(a), sin(a)) * r;
    at = base + vec2(grU(cell.x, cell.y, c), grU(cell.x, cell.y, c + 1u)) * CELL + splay * 0.16;
    tuft = 0.7 + 0.6 * grU(cell.x, cell.y, c + 2u);
  }
  vec3 root = vec3(at.x, 0.0, at.y);
  for (int b = 0; b < 4; b++) {
    vec4 x = uBare[b];
    // a soft edge: blades thin out over 15 cm toward a bare patch, as a lawn does at a path
    float inside = min(min(root.x - x.x, x.z - root.x), min(root.z - x.y, x.w - root.z));
    if (inside > -0.15 * hash12(at * 91.0)) return;
  }
  vec2 q = at / uVary.x; float vary = uVary.y;
  float lush = grNoise(q, seed) * 0.65 + grNoise(q * 2.7 + vec2(3.1, 7.7), seed) * 0.35;
  float thin = smoothstep(0.5, 0.85, grNoise(q * 1.6 + vec2(11.3, 2.9), seed + 1u)) * vary;
  float dry = clamp(uLook.w + vary * 1.2 * (grNoise(q * 0.8 + vec2(5.5, 13.1), seed + 2u) - 0.5) + 0.3 * thin, 0.0, 1.0);
  float dist = max(length(root - uEye), 0.3);
  float full = uLook.z / (uMore.y * uMore.w);
  float keep = min(1.0, full / dist);
  float share = uLook.x * keep * CELL * CELL * (1.0 - 0.35 * thin);
  float fj = float(j);
  if (fj >= share) return;
  float edge = clamp((share - fj) / max(1.0, share * 0.08), 0.0, 1.0);
  float sink = 1.0 - smoothstep(uMore.x * 0.8, uMore.x, dist);
  if (sink <= 0.0) return;
  float tall = exp2(vary * (3.2 * (lush - 0.5) - 0.8 * thin));
  float h = uLook.y * tall * tuft * (0.45 + 0.9 * grNext(rs)) * sink;
  // a pumpkin set on the lawn squashes what's under it: none beneath, short and leaning out round the edge
  vec2 outward = vec2(0.0);
  for (int i = 0; i < MAXP; i++) {
    if (i >= uNP) break;
    vec2 o = root.xz - uPC[i].xz;
    float d = length(o), R = uPGeo[i].x;
    if (uPC[i].y - R * uPGeo[i].y > 0.12 || d > R * 1.4) continue;
    if (d < R * 0.75) return;
    float k = smoothstep(R * 0.75, R * 1.4, d);
    h *= 0.35 + 0.65 * k;
    outward += o / max(d, 1e-4) * (1.0 - k) * 0.9;
  }
  bool stem = grNext(rs) < 0.03;
  if (stem) h *= 1.9 + 0.6 * grNext(rs);
  float yaw = grNext(rs) * 6.2831853;
  vec2 face = vec2(cos(yaw), sin(yaw));
  vec2 own = face * (0.08 + 0.45 * grNext(rs) * grNext(rs)) + splay * 0.35;
  if (stem) own *= 0.4;
  float phase = grNext(rs), give = 0.75 + 0.25 * grNext(rs);
  float hz = uMore.z * (0.8 + 0.4 * phase);
  vec2 lean = own + outward + windLean(root + vec3(0.0, h, 0.0), uTime, hz, phase, int(seed)) * give * uVary.w;

  int segs = uBand.z;
  int pair = vi / 2;
  float v = min(float(pair) / float(segs), 1.0);
  float side = (vi & 1) == 1 ? 1.0 : -1.0;
  float width = uLook.z, wk = edge / keep;
  float w = width * wk * (1.0 - pow(v, 1.6)) * (0.7 + 0.6 * grNext(rs));
  float minw = uMore.y * uMore.w * dist;
  if (stem) w = max(width * 0.4, minw) * edge + width * wk * 0.9 * sin(PI * clamp((v - 0.68) / 0.32, 0.0, 1.0));
  vec3 across = vec3(-face.y, 0.0, face.x);
  vec3 p = grCurve(root, h, lean, v) + across * side * w * 0.5;
  float dv = 1.0 / float(segs);
  vec3 tng = grCurve(root, h, lean, min(v + dv * 0.5, 1.0)) - grCurve(root, h, lean, max(v - dv * 0.5, 0.0));
  vec3 n = normalize(cross(across, tng));
  n = normalize(n + across * side * 0.55);
  n = normalize(mix(n, vec3(0.0, 1.0, 0.0), smoothstep(6.0, 40.0, dist) * 0.65));
  gl_Position = uVP * vec4(p, 1.0);
  vW = p; vN = n; vT = v;
  // October: greener in the lush patches, straw where it's thin, darker at the root where blades shade each other
  vec3 green = vec3(0.05, 0.1, 0.025), dryc = vec3(0.3, 0.24, 0.11);
  bool isDry = grNext(rs) < dry; float dryAmt = 0.6 + 0.4 * grNext(rs);
  vec3 g = mix(green, green * vec3(1.3, 1.08, 0.7), (1.0 - lush) * vary);
  vec3 alb = mix(g, dryc, isDry ? dryAmt : 0.0) * (0.8 + 0.4 * grNext(rs));
  alb *= mix(0.3, 1.0, pow(v, 0.6)) * (1.0 + 0.25 * v * v);
  if (stem) alb = mix(alb, dryc * vec3(0.7, 0.58, 0.56), smoothstep(0.6, 0.8, v));
  vAlb = alb;
}`;

export const GRASS_FS = /* glsl */`
${COMMON}
in vec3 vW, vN, vAlb;
in float vT;
out vec4 frag;
void main() {
  vec3 n = normalize(vN);
  if (!gl_FrontFacing) n = -n;
  vec3 v = normalize(uEye - vW);
  // cheap matter, as liminality lights it: a share of the light behind comes through the blade, no glint
  vec3 col = shade(vW, n, v, vAlb, 0.9, 0.0, 0.3, 0.45, -1);
  // a lawn at night reads by its tips catching the moon: silver on the blade ends, more looking toward it
  float fwd = 0.35 + 0.65 * pow(max(dot(-v, uMoonDir), 0.0), 2.0);
  col += uMoon * (vAlb * 4.0 + vec3(0.04, 0.055, 0.08)) * vT * vT * fwd;
  frag = finish(mist(col, vW));
}`;

// ---- the light shafts out of the eyes: a short march through the mist round each pumpkin

export const BEAMS_FS = /* glsl */`
${COMMON}
in vec2 vUv;
uniform sampler2D uDepth;
uniform mat4 uInvVP;
out vec4 frag;
void main() {
  float z = texture(uDepth, vUv).r;
  vec4 a = uInvVP * vec4(vUv * 2.0 - 1.0, z * 2.0 - 1.0, 1.0);
  vec3 p = a.xyz / a.w;
  vec3 d = p - uEye;
  float D = length(d);
  d /= D;
  vec3 sum = vec3(0.0);
  float jit = hash12(gl_FragCoord.xy + fract(uTime * 7.0) * 113.0);
  for (int i = 0; i < MAXP; i++) {
    if (i >= uNP) break;
    vec3 o = uEye - uPC[i];
    float R = 1.8;
    float b = dot(o, d), c = dot(o, o) - R * R, disc = b * b - c;
    if (disc <= 0.0) continue;
    float s = sqrt(disc), t0 = max(0.0, -b - s), t1 = min(D, -b + s);
    if (t1 <= t0) continue;
    float dt = (t1 - t0) / 14.0;
    for (int k = 0; k < 14; k++) {
      vec3 x = uEye + d * (t0 + (float(k) + jit) * dt);
      // inside the shell is candle, not mist: seen through a mouth it would add up to a white sun
      vec3 q = uPRot[i] * (x - uPC[i]);
      if (dot(vec3(q.x, q.y / uPGeo[i].y, q.z), vec3(q.x, q.y / uPGeo[i].y, q.z)) < uPGeo[i].x * uPGeo[i].x * 1.44) continue;
      // and right at a hole the inverse square runs away: mist that close is a pinprick, so soften it
      float qq = dot(q, q);
      vec3 L;
      sum += pumpkinLight(i, x, L) * dt * qq / (qq + 0.06);
    }
  }
  frag = vec4(sum * uMist.w * 0.05, 1.0);
}`;

// ---- bloom (liminality's frame/post.ts: the 13-tap down, the tent up) and the display

export const BLOOM_DOWN_FS = /* glsl */`
in vec2 vUv;
uniform sampler2D uSrc;
uniform vec2 uTexel;
out vec4 frag;
vec3 s(vec2 uv) { return texture(uSrc, uv).rgb; }
void main() {
  vec2 t = uTexel, uv = vUv;
  vec3 a = s(uv + t * vec2(-2, -2)), b = s(uv + t * vec2(0, -2)), c = s(uv + t * vec2(2, -2));
  vec3 d = s(uv + t * vec2(-1, -1)), e = s(uv + t * vec2(1, -1));
  vec3 f = s(uv + t * vec2(-2, 0)), g = s(uv), h = s(uv + t * vec2(2, 0));
  vec3 i = s(uv + t * vec2(-1, 1)), j = s(uv + t * vec2(1, 1));
  vec3 k = s(uv + t * vec2(-2, 2)), l = s(uv + t * vec2(0, 2)), m = s(uv + t * vec2(2, 2));
  vec3 o = (d + e + i + j) * 0.125 + (a + c + k + m) * 0.03125 + (b + f + h + l) * 0.0625 + g * 0.125;
  frag = vec4(min(o, vec3(40.0)), 1.0);
}`;

export const BLOOM_UP_FS = /* glsl */`
in vec2 vUv;
uniform sampler2D uLow;
uniform vec2 uTexel;
out vec4 frag;
void main() {
  vec2 t = uTexel, uv = vUv;
  vec3 u = texture(uLow, uv).rgb * 4.0;
  u += (texture(uLow, uv + vec2(t.x, 0)).rgb + texture(uLow, uv - vec2(t.x, 0)).rgb + texture(uLow, uv + vec2(0, t.y)).rgb + texture(uLow, uv - vec2(0, t.y)).rgb) * 2.0;
  u += texture(uLow, uv + t).rgb + texture(uLow, uv - t).rgb + texture(uLow, uv + vec2(t.x, -t.y)).rgb + texture(uLow, uv - vec2(t.x, -t.y)).rgb;
  frag = vec4(u / 16.0, 1.0);
}`;

export const DISPLAY_FS = /* glsl */`
in vec2 vUv;
uniform sampler2D uScene, uBloom, uBeams, uZ;
uniform vec4 uD;    // time, exposure, bloom mix, grain
uniform vec2 uFlags; // bloom on, beams on
uniform vec4 uPhoto; // photo mode: filter, focus distance (m), widest blur (px), -
uniform vec2 uTexel;
out vec4 frag;
vec3 aces(vec3 x) { return clamp((x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14), 0.0, 1.0); }
float hash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
// metres from the eye, from the depth buffer (near 0.03, far 80)
float metres(vec2 uv) { float d = texture(uZ, uv).r * 2.0 - 1.0; return 4.8 / (80.03 - d * 79.97); }
// how blurred a point is, in pixels: none at the focus, all of it well in front or far behind
float coc(float z) { return uPhoto.z * clamp(abs(1.0 - uPhoto.y / z) * 1.6, 0.0, 1.0); }
// depth of field gathered from a spiral of taps: a tap counts where its own blur reaches this far, and a tap behind
// a sharp point can't smear over it
vec3 focus(vec3 c0) {
  float z0 = metres(vUv), r0 = coc(z0);
  vec3 sum = c0;
  float n = 1.0, turn = hash(gl_FragCoord.xy) * 6.283;
  for (int i = 0; i < 28; i++) {
    float f = (float(i) + 0.5) / 28.0, r = sqrt(f) * uPhoto.z, a = float(i) * 2.39996 + turn;
    vec2 uv = vUv + vec2(cos(a), sin(a)) * r * uTexel;
    float z = metres(uv), ri = coc(z);
    float reach = z > z0 ? min(ri, r0) : ri;
    float w = clamp(reach - r + 1.0, 0.0, 1.0);
    sum += texture(uScene, uv).rgb * w;
    n += w;
  }
  return sum / n;
}
void main() {
  vec3 c = texture(uScene, vUv).rgb;
  if (uPhoto.z > 0.5) c = focus(c);
  if (uFlags.y > 0.5) c += texture(uBeams, vUv).rgb;
  if (uFlags.x > 0.5) c = mix(c, texture(uBloom, vUv).rgb, uD.z);
  c *= uD.y;
  vec2 q = vUv - 0.5;
  c *= 1.0 - dot(q, q) * 0.9;
  c = aces(c);
  c = pow(c, vec3(1.0 / 2.2));
  float grain = uD.w, f = uPhoto.x;
  if (f > 0.5) {
    float l = dot(c, vec3(0.299, 0.587, 0.114));
    if (f < 1.5) c = vec3(smoothstep(0.02, 0.9, l));                                         // mono
    else if (f < 2.5) { c = mix(vec3(0.12, 0.07, 0.03), vec3(1.0, 0.86, 0.62), smoothstep(0.0, 0.9, l)); c *= 1.0 - dot(q, q) * 0.8; grain += 0.03; } // sepia
    else if (f < 3.5) {                                                                       // night vision
      // gain with a soft shoulder, not a clamp: a lit pumpkin keeps its ribs and the shadows stay dark
      float v = smoothstep(0.02, 1.0, 1.0 - exp(-l * 2.4));
      c = mix(vec3(0.2, 0.95, 0.3) * v, vec3(0.75, 1.0, 0.75), smoothstep(0.7, 1.0, v) * 0.5);
      c *= 0.92 + 0.08 * sin(gl_FragCoord.y * 1.7);
      c *= smoothstep(0.62, 0.42, length(q * vec2(1.6, 1.0)));
      grain += 0.06;
    }
    else c = mix(vec3(l) * vec3(0.62, 0.9, 1.0), c, smoothstep(0.35, 0.8, c.r - c.b) * 0.85);  // haunted: cold, the glow kept
  }
  c += (hash(vUv * 1000.0 + uD.x) - 0.5) * grain;
  frag = vec4(c, 1.0);
}`;

// ---- the stencil sheet as paper, from flat in front of a pumpkin to wrapped on its skin

export const PAPER_VS = /* glsl */`
layout(location = 0) in vec3 aPos;
layout(location = 1) in vec3 aNormal;
layout(location = 2) in vec2 aUv;
uniform mat4 uVP;
out vec3 vW, vN;
out vec2 vUv;
void main() {
  vW = aPos; vN = aNormal; vUv = aUv;
  gl_Position = uVP * vec4(aPos, 1.0);
}`;

export const PAPER_FS = /* glsl */`
${COMMON}
in vec3 vW, vN;
in vec2 vUv;
uniform sampler2D uPaper;
uniform float uAlpha;
out vec4 frag;
void main() {
  vec3 ink = pow(texture(uPaper, vUv).rgb, vec3(2.2));
  vec3 n = normalize(vN);
  vec3 v = normalize(uEye - vW);
  if (dot(n, v) < 0.0) n = -n;
  // paper: matte, a little light through it from behind
  vec3 col = shade(vW, n, v, ink * 0.85, 0.9, 0.01, 0.4, 0.25, -1);
  vec4 f = finish(col);
  frag = vec4(f.rgb, uAlpha);
}`;
